import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Star, MessageSquare, CheckCircle, Clock, ShieldAlert, Send, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import client from '@/services/client';
import Pagination, { usePagination } from '@/components/shared/Pagination';

const STATE_META = {
  offer_signed: { label: 'Offer signed', tone: 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400' },
  selected:     { label: 'Selected',     tone: 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400' },
  rejected:     { label: 'Not selected', tone: 'bg-slate-100 dark:bg-zinc-900 text-slate-600 dark:text-zinc-400' },
};

const MODERATION_META = {
  org_preview:   { label: 'In 24-hour org preview', icon: Clock,        tone: 'text-amber-600 dark:text-amber-400' },
  live:          { label: 'Live',                    icon: CheckCircle, tone: 'text-emerald-600 dark:text-emerald-400' },
  flagged:       { label: 'Under moderation',        icon: ShieldAlert, tone: 'text-orange-600 dark:text-orange-400' },
  admin_revoked: { label: 'Removed by admin',        icon: ShieldAlert, tone: 'text-red-600 dark:text-red-400' },
  pending_llm:   { label: 'Processing…',              icon: Clock,        tone: 'text-slate-500' },
};

function StarInput({ value, onChange }) {
  return (
    <div className="flex items-center gap-1" data-testid="review-rating-input">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          data-testid={`review-star-${n}`}
          className="p-1"
          aria-label={`Rate ${n} star`}
        >
          <Star className={`w-6 h-6 ${n <= value ? 'fill-amber-400 text-amber-400' : 'text-slate-300 dark:text-zinc-600'}`} />
        </button>
      ))}
    </div>
  );
}

function StarsReadonly({ value }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`w-3.5 h-3.5 ${n <= value ? 'fill-amber-400 text-amber-400' : 'text-slate-300 dark:text-zinc-700'}`} />
      ))}
    </span>
  );
}

export default function ApplicantReviews() {
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null); // { match, existingReview? }
  const [form, setForm] = useState({ rating: 5, title: '', body: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await client.get('/journey/applicant/my-applications');
      setJourneys(res.data?.applications || []);
    } catch {
      toast.error('Could not load your journeys');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const terminalMatches = useMemo(() => {
    return (journeys || []).filter((m) => {
      const outcome = m.final_outcome || m?.journey?.final_outcome;
      const stage = m.current_stage || m?.journey?.current_stage;
      return outcome === 'rejected' || outcome === 'selected' || stage === 'offer_signed';
    });
  }, [journeys]);
  const pg = usePagination(terminalMatches, 20);

  const openReview = async (m) => {
    try {
      const res = await client.get(`/reviews/match/${m.match_id || m.id}/mine`);
      setDrawer({ match: m, existingReview: res.data?.review || null });
      if (res.data?.review) {
        setForm({
          rating: res.data.review.rating || 5,
          title: res.data.review.title || '',
          body: res.data.review.body || '',
        });
      } else {
        setForm({ rating: 5, title: '', body: '' });
      }
    } catch {
      toast.error('Could not open the review');
    }
  };

  const submit = async () => {
    if ((form.body || '').trim().length < 20) {
      toast.error('Please share at least a couple of sentences (20+ characters).');
      return;
    }
    if (!drawer) return;
    setSubmitting(true);
    try {
      const matchId = drawer.match.match_id || drawer.match.id;
      const res = await client.post(`/reviews/match/${matchId}`, {
        rating: form.rating,
        title: form.title.trim(),
        body: form.body.trim(),
      });
      const ms = res.data?.moderation_status;
      if (ms === 'org_preview') {
        toast.success('Submitted! The organization will see your review within 24 hours, then it goes live.');
      } else if (ms === 'flagged') {
        toast.info('Submitted — our team will review it before it goes live.');
      } else {
        toast.success('Review submitted');
      }
      setDrawer(null);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to submit review';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="applicant-reviews-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1 flex items-center gap-2">
          <MessageSquare className="w-6 h-6" /> Reviews
        </h1>
        <p className="text-slate-500 dark:text-zinc-500 text-sm">
          Share your experience once your application closes. Constructive feedback helps other candidates and pushes teams to level up.
          Please don&apos;t share specific interview questions — that&apos;s what gets flagged.
        </p>
      </div>

      {loading ? (
        <p className="p-8 text-center text-slate-400 text-sm">Loading…</p>
      ) : terminalMatches.length === 0 ? (
        <div className="p-8 text-center bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg">
          <p className="text-slate-500 dark:text-zinc-400 text-sm">
            When one of your applications reaches a final outcome, you&apos;ll be able to review the experience here.
          </p>
        </div>
      ) : (
        <>
        <ul className="space-y-3" data-testid="applicant-reviews-list">
          {pg.pageItems.map((m) => {
            const outcome = m.final_outcome || m?.journey?.final_outcome;
            const stage = m.current_stage || m?.journey?.current_stage;
            const stateKey = stage === 'offer_signed' ? 'offer_signed' : (outcome || stage);
            const meta = STATE_META[stateKey] || STATE_META.rejected;
            return (
              <li key={m.match_id || m.id}
                  className="p-4 bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg flex items-start justify-between gap-4"
                  data-testid={`applicant-review-row-${m.match_id || m.id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 dark:text-white">{m.job_title || 'Role'}</p>
                  <p className="text-[13px] text-slate-500 dark:text-zinc-500 mt-0.5">{m.org_name || m.organization_name || ''}</p>
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium mt-2 ${meta.tone}`}>
                    {meta.label}
                  </span>
                </div>
                <Button size="sm" onClick={() => openReview(m)} data-testid={`applicant-review-open-${m.match_id || m.id}`}>
                  Leave a review
                </Button>
              </li>
            );
          })}
        </ul>
        <Pagination {...pg.paginationProps} testIdPrefix="applicant-reviews-pg" />
        </>
      )}

      {drawer && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setDrawer(null)}>
          <div className="flex-1 bg-black/40" />
          <aside
            className="w-full max-w-[560px] h-full overflow-y-auto bg-white dark:bg-[#0d0d10] border-l border-slate-200 dark:border-zinc-800 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="applicant-review-drawer"
          >
            <div className="sticky top-0 flex items-center justify-between px-5 py-3 border-b border-slate-100 dark:border-zinc-800 bg-white/95 dark:bg-[#0d0d10]/95 backdrop-blur">
              <div>
                <p className="text-[11px] text-slate-400 uppercase tracking-wide">Review</p>
                <h3 className="text-[15px] font-semibold text-slate-900 dark:text-white truncate max-w-[360px]">
                  {drawer.match.job_title || 'Role'} · {drawer.match.org_name || drawer.match.organization_name || ''}
                </h3>
              </div>
              <button
                onClick={() => setDrawer(null)}
                className="grid place-items-center h-8 w-8 rounded hover:bg-slate-100 dark:hover:bg-zinc-900 text-slate-500"
                data-testid="applicant-review-drawer-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {drawer.existingReview && (
                <div className="flex items-start gap-2 p-3 bg-slate-50 dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded-md">
                  {(() => {
                    const ms = drawer.existingReview.moderation_status;
                    const Meta = MODERATION_META[ms] || MODERATION_META.pending_llm;
                    const Icon = Meta.icon;
                    return (
                      <>
                        <Icon className={`w-4 h-4 mt-0.5 ${Meta.tone}`} />
                        <div className="text-[12.5px] text-slate-700 dark:text-zinc-300">
                          <p className="font-medium">{Meta.label}</p>
                          {drawer.existingReview.moderation_reason && (
                            <p className="text-slate-500 dark:text-zinc-500 mt-0.5">{drawer.existingReview.moderation_reason}</p>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              <div>
                <label className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-zinc-400">Overall rating</label>
                <StarInput value={form.rating} onChange={(v) => setForm((f) => ({ ...f, rating: v }))} />
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-zinc-400">Headline (optional)</label>
                <input
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  maxLength={120}
                  placeholder="e.g. Fast process, transparent recruiters"
                  className="w-full mt-1 px-3 py-2 rounded-md bg-white dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 text-[13.5px] text-slate-900 dark:text-white"
                  disabled={!!drawer.existingReview}
                  data-testid="applicant-review-title-input"
                />
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-zinc-400">Your experience</label>
                <textarea
                  value={form.body}
                  onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                  maxLength={2000}
                  rows={7}
                  placeholder="How was communication? Was the process fair and timely? Would you apply again?"
                  className="w-full mt-1 px-3 py-2 rounded-md bg-white dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 text-[13.5px] text-slate-900 dark:text-white"
                  disabled={!!drawer.existingReview}
                  data-testid="applicant-review-body-input"
                />
                <p className="mt-1 text-[11px] text-slate-400 dark:text-zinc-500">
                  Do NOT share specific interview questions, coding challenges, or answers — those reviews get flagged.
                </p>
              </div>

              {!drawer.existingReview && (
                <Button
                  onClick={submit}
                  disabled={submitting}
                  className="w-full"
                  data-testid="applicant-review-submit-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {submitting ? 'Submitting…' : 'Submit review'}
                </Button>
              )}

              {drawer.existingReview && (
                <div className="pt-2 flex items-center gap-2 text-[12px] text-slate-500 dark:text-zinc-500">
                  <StarsReadonly value={drawer.existingReview.rating} />
                  <span>· submitted {new Date(drawer.existingReview.created_at).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
